import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { rupiah, num, packDisplay } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Truck, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Fleet() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ code: "", name: "", vehicle_no: "", vehicle_type: "Mobil Box", phone: "", email: "", target: "" });
  const [selected, setSelected] = useState(null);
  const [stock, setStock] = useState([]);

  const load = useCallback(async () => {
    const r = await api.get("/sales-agents");
    setRows(r.data);
    setSelected((prev) => prev || (r.data.length ? r.data[0] : null));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!selected) return;
    api.get(`/sales-agents/${selected.sales_id}/stock`).then((r) => setStock(r.data)).catch(() => setStock([]));
  }, [selected]);

  const save = async () => {
    try {
      if (editing) await api.put(`/sales-agents/${editing.sales_id}`, form);
      else await api.post("/sales-agents", form);
      toast.success("Sales tersimpan"); setOpen(false); setEditing(null); load();
      setForm({ code: "", name: "", vehicle_no: "", vehicle_type: "Mobil Box", phone: "", email: "", target: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const openEdit = (s) => { setEditing(s); setForm({ email: "", ...s }); setOpen(true); };

  const remove = async (s) => {
    if (!window.confirm(`Hapus sales ${s.name}? Stok armadanya tetap tercatat di histori.`)) return;
    try {
      await api.delete(`/sales-agents/${s.sales_id}`);
      toast.success("Sales dihapus");
      if (selected?.sales_id === s.sales_id) { setSelected(null); setStock([]); }
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal menghapus"); }
  };

  const totalValue = stock.reduce((a, b) => a + b.value, 0);

  return (
    <div data-testid="page-fleet" className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">Master Data</div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Sales & Armada</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-slate-900 hover:bg-slate-800" data-testid="btn-new-sales" onClick={() => setEditing(null)}><Plus className="h-4 w-4 mr-1" />Tambah Sales</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{editing ? "Ubah Sales" : "Tambah Sales"}</DialogTitle><DialogDescription className="sr-only">Form data sales/armada</DialogDescription></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Kode</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} disabled={!!editing} data-testid="input-code" /></div>
              <div><Label>Nama</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="input-sales-name" /></div>
              <div><Label>No. Kendaraan</Label><Input value={form.vehicle_no} onChange={(e) => setForm({ ...form, vehicle_no: e.target.value })} /></div>
              <div><Label>Jenis Kendaraan</Label><Input value={form.vehicle_type} onChange={(e) => setForm({ ...form, vehicle_type: e.target.value })} /></div>
              <div><Label>No. HP</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div><Label>Email Akun <span className="text-xs text-slate-400">(untuk login role Sales)</span></Label><Input type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="sales@email.com" data-testid="input-sales-email" /></div>
              <div><Label>Target Omzet/Bulan <span className="text-xs text-slate-400">(Rp)</span></Label><Input type="number" value={form.target || ""} onChange={(e) => setForm({ ...form, target: e.target.value })} placeholder="0" data-testid="input-sales-target" /></div>
            </div>
            <DialogFooter><Button onClick={save} data-testid="btn-save-sales" className="bg-slate-900 hover:bg-slate-800">Simpan</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid lg:grid-cols-[320px_1fr] gap-6">
        <Card>
          <CardHeader><CardTitle className="text-base">Daftar Sales</CardTitle></CardHeader>
          <CardContent className="p-0">
            {rows.map((s) => (
              <div key={s.sales_id} data-testid={`select-sales-${s.code}`}
                className={`w-full text-left p-4 border-b flex items-center gap-3 hover:bg-slate-50 cursor-pointer ${selected?.sales_id === s.sales_id ? "bg-slate-50 border-l-4 border-l-emerald-500" : ""}`}
                onClick={() => setSelected(s)}>
                <div className="h-10 w-10 rounded bg-slate-900 grid place-items-center"><Truck className="h-5 w-5 text-emerald-400" /></div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium">{s.name}</div>
                  <div className="text-xs text-slate-500 font-mono">{s.code} · {s.vehicle_no}</div>
                </div>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); openEdit(s); }} data-testid={`edit-sales-${s.code}`}><Pencil className="h-3.5 w-3.5" /></Button>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); remove(s); }} data-testid={`delete-sales-${s.code}`}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></Button>
              </div>
            ))}
            {rows.length === 0 && <div className="p-6 text-center text-slate-400 text-sm">Belum ada sales</div>}
          </CardContent>
        </Card>

        {selected && (
          <Card>
            <CardHeader className="flex-row items-start justify-between space-y-0">
              <div>
                <CardTitle className="text-lg">{selected.name}</CardTitle>
                <div className="text-xs text-slate-500 mt-1">{selected.vehicle_type} · {selected.vehicle_no} · {selected.phone}</div>
              </div>
              <div className="text-right">
                <div className="text-xs font-mono uppercase tracking-wider text-slate-500">Nilai Muatan</div>
                <div className="font-mono font-bold text-2xl text-slate-900">{rupiah(totalValue)}</div>
              </div>
            </CardHeader>
            <CardContent>
              <table className="w-full text-sm">
                <thead className="text-xs font-mono uppercase tracking-wider text-slate-500 border-b">
                  <tr><th className="py-2 text-left">SKU</th><th className="text-left">Nama</th><th className="text-right">Qty</th><th className="text-right">Nilai</th></tr>
                </thead>
                <tbody>
                  {stock.map((s) => (
                    <tr key={s.product_id} className="border-b">
                      <td className="py-2 font-mono text-xs">{s.sku}</td>
                      <td>{s.name}</td>
                      <td className="text-right font-mono">{num(s.qty)} {s.unit}{packDisplay(s.qty, s.unit, s.packs) && <div className="text-[10px] text-slate-400">{packDisplay(s.qty, s.unit, s.packs)}</div>}</td>
                      <td className="text-right font-mono">{rupiah(s.value)}</td>
                    </tr>
                  ))}
                  {stock.length === 0 && <tr><td colSpan={4} className="py-8 text-center text-slate-400">Tidak ada stok di armada</td></tr>}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
